import React, { useState, useEffect, useMemo } from 'react';
import DataTable from '../components/common/DataTable';
import { DataForm } from '../components/common/DataForm';
import SearchBar from '../components/common/SearchBar';
import PageHeader from '../components/common/PageHeader';
import Pagination from '../components/common/Pagination';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import useDebouncedSearch from '../hooks/useDebouncedSearch';
import { Image as ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { GalleryService } from '../services/gallery.service';
import { MediaServices } from '../services/media.service';
import { MEDIA_BASE_URL } from '../lib/env';
import { getFullImageUrl } from '../utils';

const COLUMNS = [
    {
        header: 'Image Preview',
        accessor: 'image_url',
        render: (row) => {
            const url = getFullImageUrl(row.image_path);
            return (
                <img
                    src={url}
                    alt={row.image_name}
                    style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px' }}
                />
            )
        }
    },
    { header: 'Image Name', accessor: 'image_name' },
    {
        header: 'Status',
        accessor: 'is_active',
        render: (row) => (
            <span className={`status-badge ${row.is_active ? 'active' : 'inactive'}`}>
                {row.is_active ? 'Active' : 'Inactive'}
            </span>
        )
    }
];

const FORM_FIELDS = [
    { label: 'Upload Image', accessor: 'image', type: 'file', fullWidth: true },
    { label: 'Image Name', accessor: 'image_name', placeholder: 'Enter short image name' },
    {
        label: 'Status', accessor: 'is_active', type: 'select', options: [
            { label: 'Active', value: 'true' },
            { label: 'Inactive', value: 'false' }
        ]
    }
];

export default function Gallery() {
    const [dataList, setDataList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingData, setEditingData] = useState(null);

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    // Search & Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const { searchQuery, setSearchQuery, debouncedQuery } = useDebouncedSearch();

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedQuery]);

    const itemsPerPage = 5;

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const res = await GalleryService.getAllGallery();
            const list = Array.isArray(res) ? res : (res?.data || []);
            const formattedList = list.map(item => ({
                ...item,
                id: item._id || item.id,
            }));
            setDataList(formattedList);
        } catch (error) {
            toast.error('Failed to load gallery images');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const filteredData = useMemo(() => {
        let result = dataList;
        if (debouncedQuery) {
            result = result.filter(item =>
                item.image_name?.toLowerCase().includes(debouncedQuery.toLowerCase())
            );
        }
        return result;
    }, [dataList, debouncedQuery]);

    const kpis = useMemo(() => {
        const total = dataList.length;
        const active = dataList.filter(d => d.is_active).length;
        return [
            { label: "Total Images", value: total, icon: <ImageIcon size={20} />, color: 'blue' },
            { label: "Active Images", value: active, icon: <ImageIcon size={20} />, color: 'green' }
        ];
    }, [dataList]);

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const paginatedData = filteredData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handleFormSubmit = async (formData) => {
        try {
            let image_url = editingData?.image_url;
            let image_path = editingData?.image_path;

            if (formData.image_file) {
                toast.loading("Uploading photo...", { id: "uploadToast" });
                const mediaRes = await MediaServices.uploadMedia(formData.image_file, "image", "gallery");
                toast.dismiss("uploadToast");
                console.log("media response is", mediaRes);
                if (mediaRes?.success && mediaRes?.data) {
                    image_url = `${MEDIA_BASE_URL}/${mediaRes.data.name}`;
                    image_path = mediaRes.data.name; // Use URL for path if backend handles it, or use mediaRes.data.path
                } else {
                    toast.error("Photo upload failed. Please try again.");
                    return;
                }
            }

            const payload = {
                image_name: formData.image_name,
                is_active: formData.is_active === 'true' || formData.is_active === true,
            };

            if (image_url) {
                payload.image_url = image_url;
                payload.image_path = image_path;
            }

            if (editingData) {
                const targetId = editingData._id || editingData.id;
                await GalleryService.updateGallery(targetId, payload);
                toast.success('Gallery image updated successfully!');
                await fetchData();
            } else {
                if (!payload.image_url) {
                    toast.error("Image file is required to create a gallery entry.");
                    return;
                }
                await GalleryService.createGallery(payload);
                toast.success('Gallery image added successfully!');
                await fetchData();
            }
            setIsFormOpen(false);
            setEditingData(null);
        } catch (error) {
            toast.dismiss("uploadToast");
            toast.error(error.response?.data?.message || 'Failed to save gallery image');
        }
    };

    const handleEdit = (row) => {
        // Convert boolean to string for the select field
        setEditingData({
            ...row,
            image: row.image_url,
            is_active: String(row.is_active)
        });
        setIsFormOpen(true);
    };

    const handleDeleteClick = (row) => {
        setDeleteTarget(row);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteExecute = async () => {
        if (!deleteTarget) return;
        try {
            const targetId = deleteTarget._id || deleteTarget.id;
            await GalleryService.deleteGallery(targetId);
            setDataList(prev => prev.filter(item => item.id !== targetId));
            toast.success('Gallery image deleted successfully!');
        } catch (error) {
            toast.error('Failed to delete gallery image');
        } finally {
            setIsDeleteModalOpen(false);
            setDeleteTarget(null);
        }
    };

    return (
        <div className="page-content">
            <PageHeader
                title="Media Gallery"
                subtitle="Manage images displayed in the website gallery"
                onAdd={() => { setEditingData(null); setIsFormOpen(true); }}
                addLabel="Add Image"
                kpis={kpis}
            />

            {isFormOpen && (
                <DataForm
                    title={editingData ? `Edit Gallery Image` : `Add New Gallery Image`}
                    fields={FORM_FIELDS}
                    initialData={editingData}
                    onSubmit={handleFormSubmit}
                    onClose={() => setIsFormOpen(false)}
                />
            )}

            <div className="table-card-container">
                <div className="table-actions-area">
                    <div className="table-actions-left">
                        <SearchBar
                            placeholder="Search images by name..."
                            value={searchQuery}
                            onChange={setSearchQuery}
                        />
                    </div>
                </div>

                <div className="table-responsive-wrapper">
                    <DataTable
                        columns={COLUMNS}
                        data={paginatedData}
                        onEdit={handleEdit}
                        onDelete={handleDeleteClick}
                        loading={isLoading}
                    />
                </div>

                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalEntries={filteredData.length}
                    itemsPerPage={itemsPerPage}
                    onPageChange={setCurrentPage}
                />
            </div>

            <DeleteConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => { setIsDeleteModalOpen(false); setDeleteTarget(null); }}
                onConfirm={handleDeleteExecute}
                title="Delete Gallery Image"
                message="Are you sure you want to delete this image from the gallery?"
                itemName={deleteTarget ? deleteTarget.image_name : ''}
            />
        </div>
    );
}